import {
  ReactElement, memo, useEffect, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { StatusBox } from '~/components';
import { getCachedDevInfo, getCachedDevInfoSync } from '~/helpers/cachedStorage';
import { formatNumberWithFractionDigits } from '~/helpers/thousandFormatNumber';
import { useWebSocketLazy, WSConn } from '~/helpers/wsConnection';
import { TermometerIcon } from '~/icons';
import { apiCall } from '~/providers';
import { ecoModeCfgs } from '../constants';
import { InfoContainer, StyledText, Text } from '../styles';

interface DamEcoModeProps {
  damId: string;
  ecoMode?: string | null;
}

function beforeWsClose(wsConn: WSConn) {
  wsConn.send({ type: 'dutUnsubscribeRealTime' });
  wsConn.send({ type: 'unsubscribeStatus' });
}

export const DamReferenceEcoMode = memo(({
  damId,
  ecoMode,
}: Readonly<DamEcoModeProps>): ReactElement => {
  const [damRealTimeInfo, setDamRealTimeInfo] = useState<{
    Temperature: number | null,
    status: string | null,
  }>();
  const [ecoCfg, setEcoCfg] = useState(ecoMode);
  const [devInfo, setDevInfo] = useState(getCachedDevInfoSync(damId));
  const [relDutInfo, setRelDutInfo] = useState<{
    REL_DUT_ID: string,
    TUSEMIN: number,
    ROOM_NAME: string,
    Temperature: number | null,
    status: string | null,
  }>();
  const wsl = useWebSocketLazy();
  const { t } = useTranslation();

  function onWsOpen(wsConn: WSConn) {
    if (relDutInfo?.REL_DUT_ID) {
      wsConn.send({ type: 'dutSubscribeRealTime', data: { DUT_ID: relDutInfo.REL_DUT_ID } });
    }
    else if (devInfo?.dam.SELF_REFERENCE) {
      wsConn.send({ type: 'subscribeStatus', data: { dam_id: damId } });
    }
  }

  function onWsMessage(message: { type: string, data: any }) {
    if (message?.type === 'dutTelemetry' && relDutInfo && message.data.dev_id === relDutInfo.REL_DUT_ID) {
      setRelDutInfo({
        ...relDutInfo,
        Temperature: message.data?.Temperature ?? null,
        status: message.data?.status ?? null,
      });
    }

    if (message?.type === 'devOnlineStatus' && relDutInfo && message.data.dev_id === relDutInfo.REL_DUT_ID) {
      setRelDutInfo({
        ...relDutInfo,
        status: message.data?.status ?? null,
      });
    }
    if (message?.type === 'damStatus' && damRealTimeInfo && message.data.status && message.data.dev_id === damId && devInfo.dam?.SELF_REFERENCE) {
      setDamRealTimeInfo({
        Temperature: message.data?.Temperature ?? null,
        status: message.data?.status ?? null,
      });
    }
  }

  async function getDevInfo(): Promise<void> {
    try {
      const data = await getCachedDevInfo(damId, {});
      setDevInfo(data);
      setEcoCfg(data.dam.ECO_CFG);

      if (!data) return;
      if (data.dam?.REL_DUT_ID) {
        const dutInfo = await apiCall('/dut/get-dut-info', { DEV_ID: data.dam.REL_DUT_ID });
        setRelDutInfo({
          REL_DUT_ID: data.dam.REL_DUT_ID,
          ROOM_NAME: dutInfo.info.ROOM_NAME ? dutInfo.info.ROOM_NAME : '',
          TUSEMIN: dutInfo.info.TUSEMIN,
          Temperature: null,
          status: null,
        });
      } else if (data.dam?.SELF_REFERENCE) {
        setDamRealTimeInfo({ Temperature: null, status: null });
      }
      wsl.start(onWsOpen, onWsMessage, beforeWsClose);
    } catch (error) {
      console.log(error);
      toast.error(t('houveErro'));
    }
  }

  useEffect(() => {
    getDevInfo();
  }, []);

  return (
    <div style={{ marginBottom: 20 }}>
      <InfoContainer>
        <h4>{t('modoEco')}</h4>
        {ecoCfg ? (
          <p>
            {t('acao')}
            {': '}
            {ecoModeCfgs[ecoCfg] ?? ecoCfg}
          </p>
        ) : (
          <p>{t('desabilitado')}</p>
        )}
      </InfoContainer>
      {relDutInfo && (
        <InfoContainer style={{ marginTop: 20 }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
          }}
          >
            <Text>
              {t('ambienteRefrigerado')}
              {' '}
              {relDutInfo.ROOM_NAME ?? relDutInfo.REL_DUT_ID}
              <a href={`${window.location.protocol}//${window.location.host}/analise/dispositivo/${relDutInfo.REL_DUT_ID}/informacoes`}>
                {`(${relDutInfo.REL_DUT_ID})`}
              </a>
            </Text>
          </div>
          <div>
            <Text>
              {t('limiteDeTemperatura')}
              {': '}
              {(relDutInfo.TUSEMIN == null) ? t('semInformacao') : `${relDutInfo.TUSEMIN} °C`}
            </Text>
          </div>
        </InfoContainer>
      )}
      {devInfo?.dam?.SELF_REFERENCE && (
        <InfoContainer style={{ marginTop: 20 }}>
          <h4>{t('modoControle')}</h4>
          <Text>{t('utilizandoDamIdComoReferencia', { damId })}</Text>
          <DeviceTemperatureStatus
            temperature={damRealTimeInfo?.Temperature}
            status={damRealTimeInfo?.status}
          />
          <div style={{ display: 'flex' }}>
            <Text>
              {`${t('limiteTemperatura')} ${devInfo.dam?.MAXIMUM_TEMPERATURE ?? '-'}ºC`}
            </Text>
          </div>
        </InfoContainer>
      )}
      {relDutInfo && (
        <DeviceTemperatureStatus
          temperature={relDutInfo?.Temperature}
          status={relDutInfo?.status}
        />
      )}
    </div>
  );
});

function DeviceTemperatureStatus({
  temperature,
  status,
}: Readonly<{
  temperature?: number | null,
  status?: string | null
}>): ReactElement {
  const { t } = useTranslation();

  return (
    <>
      <StyledText style={{ textAlign: 'left' }}>
        <TermometerIcon />
        {' '}
        {t('temperaturaAmbiente')}
      </StyledText>
      <div>
        <StyledText fontSize="42px">
          {(temperature == null) ? '-' : `${formatNumberWithFractionDigits(temperature)}°C`}
        </StyledText>
      </div>
      <div style={{ display: 'flex' }}>
        <Text>
          <StatusBox status={status ?? 'OFFLINE'}>
            {status ?? 'OFFLINE'}
          </StatusBox>
        </Text>
      </div>
    </>
  );
}
